import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Clock, MessageCircle, Send } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useSite } from "@/site/SiteContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t, lang } = useLang();
  const { contact } = useSite();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, lang });
      toast.success(t("contact.success"));
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  const waText = encodeURIComponent(
    `${t("brand")} — ${t("hero.cta1")}: ${form.name || ""} ${form.message || ""}`.trim(),
  );
  const waLink = `https://wa.me/${contact.whatsapp}?text=${waText}`;

  return (
    <section id="contact" className="relative py-16 md:py-36 px-6 md:px-12 bg-background grain">
      <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20">
        {/* Info */}
        <div>
          <Reveal>
            <Eyebrow>{t("contact.badge")}</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl text-foreground leading-tight">
              {t("contact.title")}
            </h2>
            <p className="mt-5 text-muted-foreground font-sans leading-relaxed max-w-md">{t("contact.subtitle")}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-12 space-y-7">
              <a
                data-testid="contact-phone"
                href={`tel:${contact.phoneTel}`}
                className="flex items-center gap-4 group"
              >
                <span className="inline-flex items-center justify-center w-12 h-12 border border-gold/30 rounded-sm text-gold group-hover:bg-gold group-hover:text-black transition-colors">
                  <Phone size={20} strokeWidth={1.5} />
                </span>
                <span className="font-sans text-foreground/85 group-hover:text-gold transition-colors text-lg">
                  {contact.phoneDisplay}
                </span>
              </a>

              <a
                data-testid="contact-email"
                href={`mailto:${contact.email}`}
                className="flex items-center gap-4 group"
              >
                <span className="inline-flex items-center justify-center w-12 h-12 border border-gold/30 rounded-sm text-gold group-hover:bg-gold group-hover:text-black transition-colors">
                  <Mail size={20} strokeWidth={1.5} />
                </span>
                <span className="font-sans text-foreground/85 group-hover:text-gold transition-colors text-lg">
                  {contact.email}
                </span>
              </a>

              <div className="flex items-start gap-4">
                <span className="inline-flex items-center justify-center w-12 h-12 border border-gold/30 rounded-sm text-gold shrink-0">
                  <Clock size={20} strokeWidth={1.5} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-gold/80 font-sans">
                    {t("contact.hoursLabel")}
                  </div>
                  <div className="font-sans text-foreground/85 mt-1">{t("contact.hoursValue")}</div>
                  <div className="font-sans text-muted-foreground text-sm">{t("contact.weekend")}</div>
                </div>
              </div>

              <a
                data-testid="contact-whatsapp"
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-black font-sans font-semibold px-6 py-3.5 rounded-sm hover:bg-[#1da851] transition-colors"
              >
                <MessageCircle size={19} />
                {t("contact.whatsapp")}
              </a>
            </div>
          </Reveal>
        </div>

        {/* Form */}
        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="bg-card border border-border p-8 md:p-10 rounded-sm"
          >
            <div className="space-y-7">
              <div>
                <input
                  data-testid="form-name"
                  name="name"
                  required
                  value={form.name}
                  onChange={onChange}
                  placeholder={t("contact.name")}
                  className="w-full bg-transparent border-b border-border focus:border-gold text-foreground px-1 py-3 placeholder:text-muted-foreground transition-colors outline-none font-sans"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <input
                  data-testid="form-phone"
                  name="phone"
                  required
                  value={form.phone}
                  onChange={onChange}
                  placeholder={t("contact.phone")}
                  className="w-full bg-transparent border-b border-border focus:border-gold text-foreground px-1 py-3 placeholder:text-muted-foreground transition-colors outline-none font-sans"
                />
                <input
                  data-testid="form-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder={t("contact.email")}
                  className="w-full bg-transparent border-b border-border focus:border-gold text-foreground px-1 py-3 placeholder:text-muted-foreground transition-colors outline-none font-sans"
                />
              </div>
              <div>
                <textarea
                  data-testid="form-message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={onChange}
                  placeholder={t("contact.message")}
                  className="w-full bg-transparent border-b border-border focus:border-gold text-foreground px-1 py-3 placeholder:text-muted-foreground transition-colors outline-none font-sans resize-none"
                />
              </div>
              <button
                data-testid="form-submit"
                type="submit"
                disabled={sending}
                className="group w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-sans font-semibold text-sm uppercase tracking-[0.15em] px-8 py-4 rounded-sm hover:bg-gold-deep transition-colors disabled:opacity-60"
              >
                {sending ? t("contact.sending") : t("contact.submit")}
                {!sending && <Send size={16} className="group-hover:translate-x-1 transition-transform" />}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
